import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import PrimaryTab from "./tabs/PrimaryTab";
import SetupTab from "./tabs/SetupTab";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import EntityInfoBar from "../../../../common/EntityInfoBar";
import { CONCURRENCY_HANDLED, getApiErrorMessage } from "../../../../common/concurrency";
import {
    EMPTY_GUID,
    ItemDataProfileRoutes,
    ItemDataEntryRoutes,
    RECORD_TYPE,
} from "../../../../const/constants";
import { NEW_RECORD_DEFAULTS } from "./options";

const ENTITY_LABEL = 'Item Data Profile';

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});

    const initialFormFields = {
        ROWID: EMPTY_GUID,
        RECORD: '',
        // ROWID of this profile's RECORDTYPE='P' item record, or empty when it has none yet. Comes
        // back with the record, so the Item Defaults button needs no lookup of its own.
        ITEMDEFAULTSROWID: '',
        PROFILECODE: '',
        DESCRIPTION: '',
        POPUPMESSAGE: '',
        COMMENT: '',
        // uWMM0003.qryIMItemDPAfterInsert seeds these four on a new record.
        ...NEW_RECORD_DEFAULTS,
        ADDTOEXPDATE: null,
        USERDEFINEDCODE1: '',
        USERDEFINEDCODE2: '',
        USERDEFINEDCODE3: '',
        USERDEFINEDCODE4: '',
        USERDEFINEDCODE5: '',
        // Columns the Delphi screen persists but has no controls for. Carried here (not rendered) so a
        // save echoes back what it loaded instead of blanking them - see the DTO's note.
        RITEM: null,
        RLOT: null,
        RSUBLOT: null,
        RRATESCHEDULE: null,
        DEFAULTPROFILE: '',
        SUBLOTPICKSEQUENCE: '',
        AUTOCREATEPRICETABLE: '',
        AUTOGENALTERNATEITEM: '',
        // Opaque optimistic-concurrency token. Loaded with the record, echoed back on save, and
        // replaced with the token the save returns. Never displayed or parsed.
        CONCURRENCYTOKEN: null,
    };

    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    /** Which tab is open. Only read to decide whether the header info bar shows; the TabPanel below
     *  stays uncontrolled (no selectedIndex prop), so nothing else changes. */
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const accessToken = props?.oidc?.user?.access_token;

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const isSaved = formFields.ROWID && formFields.ROWID !== EMPTY_GUID;

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => ({
            ...prevFields,
            [name]: value
        }));
    }, []);

    const mapResponseToFormFields = useCallback((item) => ({
        ROWID: item.RowId || EMPTY_GUID,
        RECORD: item.RecordNumber || '',
        ITEMDEFAULTSROWID: item.ItemDefaultsRowId || '',
        PROFILECODE: item.ProfileCode || '',
        DESCRIPTION: item.Description || '',
        POPUPMESSAGE: item.PopupMessage || '',
        COMMENT: item.Comment || '',
        STORAGESTARTDATE: item.StorageStartDate || NEW_RECORD_DEFAULTS.STORAGESTARTDATE,
        RESTARTSTORAGEDATES: item.RestartStorageDates || NEW_RECORD_DEFAULTS.RESTARTSTORAGEDATES,
        EXPDATEBASEDON: item.ExpDateBasedOn || NEW_RECORD_DEFAULTS.EXPDATEBASEDON,
        ADDTOEXPDATE: item.AddToExpDate ?? null,
        RATESCHEDULEFILTER: item.RateScheduleFilter || NEW_RECORD_DEFAULTS.RATESCHEDULEFILTER,
        USERDEFINEDCODE1: item.UserDefinedCode1 || '',
        USERDEFINEDCODE2: item.UserDefinedCode2 || '',
        USERDEFINEDCODE3: item.UserDefinedCode3 || '',
        USERDEFINEDCODE4: item.UserDefinedCode4 || '',
        USERDEFINEDCODE5: item.UserDefinedCode5 || '',
        RITEM: item.RItem ?? null,
        RLOT: item.RLot ?? null,
        RSUBLOT: item.RSublot ?? null,
        RRATESCHEDULE: item.RRateSchedule ?? null,
        DEFAULTPROFILE: item.DefaultProfile || '',
        SUBLOTPICKSEQUENCE: item.SubLotPickSequence || '',
        AUTOCREATEPRICETABLE: item.AutoCreatePriceTable || '',
        AUTOGENALTERNATEITEM: item.AutoGenAlternateItem || '',
        CONCURRENCYTOKEN: item.ConcurrencyToken || null,
    }), []);

    const loadItemDataProfileById = useCallback(async (rowId) => {
        if (!rowId || rowId === EMPTY_GUID) {
            return;
        }

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.ItemDataProfile.GetItemDataProfile}?rowid=${rowId}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                setformFields(mapResponseToFormFields(response.data));
            }
        } catch (error) {
            console.error('Error loading Item Data Profile:', error);
            notify(getApiErrorMessage(error, `Failed to load ${ENTITY_LABEL} data.`), 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken, mapResponseToFormFields]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadItemDataProfileById(rowId);
        }
    }, [props.id, loadItemDataProfileById]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoadPanelVisible(true);

        try {
            const isUpdate = !!isSaved;
            const payload = {
                RowId: isUpdate ? formFields.ROWID : EMPTY_GUID,
                KeySequence: 0,
                ProfileCode: formFields.PROFILECODE || "",
                Description: formFields.DESCRIPTION || "",
                PopupMessage: formFields.POPUPMESSAGE || "",
                Comment: formFields.COMMENT || "",
                StorageStartDate: formFields.STORAGESTARTDATE || "",
                RestartStorageDates: formFields.RESTARTSTORAGEDATES || "",
                ExpDateBasedOn: formFields.EXPDATEBASEDON || "",
                AddToExpDate: formFields.ADDTOEXPDATE === '' ? null : formFields.ADDTOEXPDATE,
                RateScheduleFilter: formFields.RATESCHEDULEFILTER || "",
                UserDefinedCode1: formFields.USERDEFINEDCODE1 || "",
                UserDefinedCode2: formFields.USERDEFINEDCODE2 || "",
                UserDefinedCode3: formFields.USERDEFINEDCODE3 || "",
                UserDefinedCode4: formFields.USERDEFINEDCODE4 || "",
                UserDefinedCode5: formFields.USERDEFINEDCODE5 || "",
                // Echoed back unchanged so the save does not blank the columns this screen cannot show.
                RItem: formFields.RITEM,
                RLot: formFields.RLOT,
                RSublot: formFields.RSUBLOT,
                RRateSchedule: formFields.RRATESCHEDULE,
                DefaultProfile: formFields.DEFAULTPROFILE || "",
                SubLotPickSequence: formFields.SUBLOTPICKSEQUENCE || "",
                AutoCreatePriceTable: formFields.AUTOCREATEPRICETABLE || "",
                AutoGenAlternateItem: formFields.AUTOGENALTERNATEITEM || "",
                // Tells the API which version of the row this edit started from. Null on insert.
                ConcurrencyToken: isUpdate ? (formFields.CONCURRENCYTOKEN || null) : null,
            };

            const endpoint = isUpdate
                ? data.ItemDataProfile.UpdateItemDataProfile
                : data.ItemDataProfile.InsertItemDataProfile;
            const method = isUpdate ? 'put' : 'post';

            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const saved = resp?.data;
            if (saved) {
                // Adopt the saved row, including a token for the version just written. Without that the
                // next save would still echo the token the page was opened with and falsely conflict.
                setformFields(mapResponseToFormFields(saved));
            }

            notify(`${ENTITY_LABEL} saved successfully.`, "success", 3000);
        } catch (err) {
            // A stale save is already handled globally by the axios interceptor, which raises the
            // "record changed - reload" dialog. Don't stack a red toast on top of it.
            if (err?.[CONCURRENCY_HANDLED]) {
                return;
            }

            notify(getApiErrorMessage(err, `Failed to save ${ENTITY_LABEL}.`), "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    const DeleteClick = () => {
        if (!isSaved) {
            notify("No record selected for deletion.", "warning", 3000);
            return;
        }

        let result = confirm(`<i>Are you sure you want to delete this ${ENTITY_LABEL}?</i>`, "Delete");
        result.then(async (dialogResult) => {
            if (dialogResult) {
                setLoadPanelVisible(true);
                try {
                    const url = `${window._env_.REACT_APP_APIS}/${data.ItemDataProfile.DeleteItemDataProfile}`;
                    await axios.post(url, formFields.ROWID, {
                        headers: {
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    // This endpoint answers a real status code, so a failure lands in catch below -
                    // no need to inspect the body for Success=false the way the older screens do.
                    notify(`${ENTITY_LABEL} deleted successfully.`, "success", 3000);
                    setformFields(initialFormFields);
                    setTimeout(() => {
                        history.push(ItemDataProfileRoutes.List);
                    }, 1000);
                } catch (error) {
                    if (error?.[CONCURRENCY_HANDLED]) {
                        return;
                    }

                    notify(getApiErrorMessage(error, `Failed to delete ${ENTITY_LABEL}.`), "error", 5000);
                } finally {
                    hideLoadPanel();
                }
            }
        });
    };

    /**
     * uWMD0003.btnItemDefaultsClick - opens the WM Item screen on this profile's default-item record
     * (Delphi uWMD0000.SetParameters, which scopes that form to R_Profile = <profile> +
     * RecordType = 'P' and inserts the row when there is none).
     *
     * Same shape as the Transaction Data Profile screen's goToDefaultTransaction: the PROFILE's rowid
     * goes in `id`, and ItemService.GetItemData resolves it the way GetTransactionByRowId does - an id
     * matching no item is retried as a profile. ITEMDEFAULTSROWID is the existence flag, the item
     * equivalent of isTransactionExist.
     *
     * Only the create route carries RProfile and profileCode - it is what tells the item screen to
     * stamp a brand-new row with this profile. The edit route must NOT carry them: the item screen
     * treats RProfile as "seed a new defaults record", so sending it on an edit overwrites the
     * record being edited. The loaded item already knows its own R_PROFILE.
     */
    const goToItemDefaults = () => {
        const profileCode = encodeURIComponent(formFields.PROFILECODE || '');
        if (formFields.ITEMDEFAULTSROWID) {
            // id is the ITEM's RowId, not the profile's - passing formFields.ROWID here sent the
            // profile key to GetItemDataByRowId, which 500s because no item carries that id.
            history.push(`${ItemDataEntryRoutes.Edit}?id=${formFields.ITEMDEFAULTSROWID}&recordType=${RECORD_TYPE.PROFILE_DEFAULTS}&RProfile=${formFields.ROWID}&profileCode=${profileCode}`);
        } else {            
            history.push(`${ItemDataEntryRoutes.Create}?recordType=${RECORD_TYPE.PROFILE_DEFAULTS}&RProfile=${formFields.ROWID}&profileCode=${profileCode}`);
        }
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
    };

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadItemDataProfileById(rowId);
        } else {
            setformFields(initialFormFields);
        }
    };

    const goBack = () => {
        history.push(ItemDataProfileRoutes.List);
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px',
                                                // Both margins zeroed, as on the Transaction screen.
                                                // .title h6 in App.scss overrides only margin-top, so the
                                                // heading keeps the browser's default bottom margin - and a
                                                // bottom margin bigger than the top one rides the text up
                                                // off the centre line the info bar beside it sits on.
                                                marginTop: '0px',
                                                marginBottom: 0
                                            }}
                                        >
                                            {ENTITY_LABEL}
                                        </h6>
                                    </div>
                                </Item>
                                {/* Primary already shows all of this on the form, so the bar would just
                                    repeat it there - same rule as the Transaction screen. */}
                                {selectedTabIndex !== 0 && (
                                    <Item location="before">
                                        <EntityInfoBar
                                            fields={[
                                                // Same order the Primary tab lists them in.
                                                { label: 'Record #', value: formFields.RECORD },
                                                { label: 'Profile', value: formFields.PROFILECODE },
                                                { label: 'Description', value: formFields.DESCRIPTION },
                                            ]}
                                        />
                                    </Item>
                                )}
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    {/* Delphi enables this only once the profile exists
                                        (qeIMItemDPAfterScroll: not AddMode and RecordsExist). */}
                                    <Button className="accordianButton"
                                        disabled={formFields.ROWID === EMPTY_GUID}
                                        text="Item Defaults" type="default" onClick={goToItemDefaults}>
                                    </Button>
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={DeleteClick} text="Delete" disabled={!isSaved} type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            {/* Two tabs, matching uWMD0003: Primary (its header-panel fields plus the
                                User Defined Codes group) and Setup. */}
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}
                                onSelectionChanged={(e) => setSelectedTabIndex(e.component.option('selectedIndex'))}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Setup">
                                    <SetupTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);
